import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { LocationAutocomplete, type LocationData } from '@/components/ui/LocationAutocomplete';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Modal, Dialog } from '@/components/ui/Modal';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { PageContainer } from '@/components/ui/PageContainer';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Table } from '@/components/ui/Table';
import { TableActionButton, TableActions } from '@/components/ui';
import { EventsService, type EventDTO } from '@/services/EventsService';
import { AttendeesService, type AttendeeDTO } from '@/services/AttendeesService';
import { AttendeeImport } from '@/components/attendees/AttendeeImport';
import { useNotificationStore } from '@/stores/notificationStore';
import { getCountryOptions } from '@/lib/countries';
import { Plus, Users, Upload, ArrowLeft, Mail, Phone, User, MapPin, Plane, FilePen, UserMinus } from 'lucide-react';

export function EventAttendees() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const eventsSvc = useMemo(() => new EventsService(), []);
  const attendeesSvc = useMemo(() => new AttendeesService(), []);
  const notifySuccess = useNotificationStore(s => s.success);
  const notifyError = useNotificationStore(s => s.error);
  
  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState<EventDTO | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState<{ items: AttendeeDTO[]; pagination: any } | null>(null);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ 
    first_name: '', 
    last_name: '',
    email: '',
    phone: '',
    city: '',
    region: '',
    country_iso2: '',
    postal_code: '',
    address_line: ''
  });
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [locationInput, setLocationInput] = useState('');
  const [isLocationDisabled, setIsLocationDisabled] = useState(false);

  async function load() {
    if (!eventId) return;
    
    setLoading(true);
    try {
      const [eventData, attendeesData] = await Promise.all([
        eventsSvc.get(eventId),
        attendeesSvc.listByEvent(eventId, page, pageSize)
      ]);
      setEvent(eventData);
      setData(attendeesData);
    } catch (e: any) {
      notifyError(e?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [eventId, page, pageSize]);

  function openCreate() {
    setEditingId(null);
    setForm({ 
      first_name: '', 
      last_name: '', 
      email: '', 
      phone: '',
      city: '',
      region: '',
      country_iso2: '',
      postal_code: '',
      address_line: ''
    });
    setLocationInput('');
    setIsLocationDisabled(false);
    setModalOpen(true);
  }

  function openEdit(attendee: AttendeeDTO) {
    setEditingId(attendee.id);
    setForm({ 
      first_name: attendee.first_name || '', 
      last_name: attendee.last_name || '', 
      email: attendee.email || '', 
      phone: attendee.phone || '',
      city: attendee.city || '',
      region: attendee.region || '',
      country_iso2: attendee.country_iso2 || '',
      postal_code: attendee.postal_code || '',
      address_line: attendee.address_line || ''
    });
    
    // Set up location state for editing - keep search field empty to avoid unwanted dropdown
    const hasLocation = attendee.city || attendee.region || attendee.country_iso2;
    setLocationInput(''); // Always keep search field empty on edit
    setIsLocationDisabled(!!hasLocation); // Disable fields if location data exists
    
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingId(null);
    setForm({ 
      first_name: '', 
      last_name: '', 
      email: '', 
      phone: '',
      city: '',
      region: '',
      country_iso2: '',
      postal_code: '',
      address_line: ''
    });
    setLocationInput('');
    setIsLocationDisabled(false);
  }

  async function submit() {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      notifyError('First name and last name are required');
      return;
    }
    
    setSaving(true);
    try {
      const locationData = {
        city: form.city || undefined,
        region: form.region || undefined,
        country_iso2: form.country_iso2 || undefined,
        postal_code: form.postal_code || undefined,
        address_line: form.address_line || undefined
      };

      if (editingId) {
        await attendeesSvc.update(editingId, { 
          first_name: form.first_name, 
          last_name: form.last_name,
          email: form.email || undefined, 
          phone: form.phone || undefined,
          ...locationData
        });
        notifySuccess('Attendee updated successfully');
      } else {
        await attendeesSvc.create(eventId!, {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email || undefined,
          phone: form.phone || undefined,
          ...locationData
        });
        notifySuccess('Attendee added successfully');
      }
      closeModal();
      await load();
    } catch (e: any) {
      notifyError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    try {
      setData(prev => prev ? { ...prev, items: prev.items.filter(i => i.id !== id), pagination: prev.pagination } : prev);
      await attendeesSvc.delete(id);
      notifySuccess('Attendee removed successfully');
      setDeleteConfirmId(null);
      await load();
    } catch (e: any) {
      notifyError(e?.message || 'Delete failed');
    }
  }

  function openGoogleMaps(lat: number, lon: number) {
    try {
      const url = `https://www.google.com/maps?q=${lat},${lon}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to open Google Maps:', error);
      notifyError('Unable to open Google Maps. Please check your popup blocker settings.');
    }
  }

  function toggleTooltip(attendeeId: string, event: React.MouseEvent) {
    event.stopPropagation();
    setActiveTooltip(activeTooltip === attendeeId ? null : attendeeId);
  }

  // Close tooltip when clicking elsewhere
  React.useEffect(() => {
    function handleClickOutside() {
      setActiveTooltip(null);
    }
    
    if (activeTooltip) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeTooltip]);

  function handleLocationSelect(locationData: LocationData) {
    // Determine if this is a specific address vs just a city
    const isSpecificAddress = locationData.formatted_address.includes(',') && 
                             (locationData.formatted_address.includes('St') || 
                              locationData.formatted_address.includes('Ave') || 
                              locationData.formatted_address.includes('Rd') || 
                              locationData.formatted_address.includes('Blvd') ||
                              /\d/.test(locationData.formatted_address.split(',')[0])); // Contains numbers in first part

    // Auto-populate all location fields from Google Places
    setForm(f => ({
      ...f,
      city: locationData.city || '',
      region: locationData.region || '',
      country_iso2: locationData.country_iso2 || '',
      postal_code: locationData.postal_code || '',
      // Populate address_line if this is a specific address (not just a city)
      address_line: isSpecificAddress ? locationData.formatted_address : f.address_line
    }));
    
    // Disable individual location fields since they're auto-populated
    setIsLocationDisabled(true);
    
    const message = isSpecificAddress 
      ? 'Address and location auto-populated from Google Places'
      : 'City location auto-populated from Google Places';
    notifySuccess(message);
  }

  function clearLocation() {
    setLocationInput('');
    setIsLocationDisabled(false);
    setForm(f => ({
      ...f,
      city: '',
      region: '',
      country_iso2: '',
      postal_code: ''
      // Keep address_line as user might want to keep it
    }));
  }

  if (loading && !event) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Breadcrumb */}
      <div className="mb-6">
        <Breadcrumb 
          items={[
            { label: 'Clients', href: '/organization/clients' },
            { label: event?.client_id || '', href: `/organization/clients/${event?.client_id}/events` },
            { label: event?.name || eventId || '', href: '#' }
          ]} 
        />
      </div>

      <PageHeader 
        title={`${event?.name || 'Event'} - Attendees`} 
        description="Manage attendees for this event"
      >
        <>
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back
          </Button>
          <Button 
            variant="secondary"
            leftIcon={<Upload className="w-4 h-4" />}
            onClick={() => setImportModalOpen(true)}
          >
            Import CSV
          </Button>
          <Button onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
            Add Attendee
          </Button>
        </>
      </PageHeader>


      {/* Attendees Table */}
      {data && (
        <Table
          data={data.items}
          columns={[
            { 
              key: 'full_name', 
              title: 'Name',
              render: (value, record) => (
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="font-medium truncate">{value}</span>
                </div>
              )
            },
            { 
              key: 'email', 
              title: 'Email',
              render: (value) => value ? (
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <a href={`mailto:${value}`} className="text-primary-600 hover:underline truncate">{value}</a>
                </div>
              ) : <span className="text-gray-400">—</span>
            },
            { 
              key: 'phone', 
              title: 'Phone',
              render: (value) => value ? (
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{value}</span>
                </div>
              ) : <span className="text-gray-400">—</span>
            },
            { 
              key: 'city', 
              title: 'City',
              render: (value) => value ? (
                <span className="truncate">{value}</span>
              ) : <span className="text-gray-400">—</span>
            },
            { 
              key: 'region', 
              title: 'State/Region',
              render: (value) => value ? (
                <span className="truncate">{value}</span>
              ) : <span className="text-gray-400">—</span>
            },
            { 
              key: 'country_iso2', 
              title: 'Country',
              render: (value) => value ? (
                <span className="truncate">{value}</span>
              ) : <span className="text-gray-400">—</span>
            },
            { 
              key: 'geocoded', 
              title: 'Location',
              align: 'center',
              width: '80px',
              render: (_, record) => {
                // Safely convert lat/lon to numbers
                const lat = record.lat != null ? Number(record.lat) : null;
                const lon = record.lon != null ? Number(record.lon) : null;
                const isGeocoded = lat != null && lon != null && !isNaN(lat) && !isNaN(lon);
                
                return isGeocoded ? (
                  <button
                    onClick={() => openGoogleMaps(lat, lon)}
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 hover:bg-green-200 transition-colors"
                    title={`Open location in Google Maps (${lat.toFixed(4)}, ${lon.toFixed(4)})`}
                  >
                    <MapPin className="w-3 h-3 text-green-600" />
                  </button>
                ) : (
                  <div 
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100"
                    title="Location not geocoded"
                  >
                    <MapPin className="w-3 h-3 text-gray-400" />
                  </div>
                );
              }
            },
            { 
              key: 'nearest_airports', 
              title: 'Nearest Airports',
              width: '200px',
              render: (_, record) => {
                const airports = record.nearest_airports || [];
                
                if (airports.length === 0) {
                  return (
                    <div 
                      className="inline-flex items-center gap-1 text-gray-400"
                      title="No airports assigned"
                    >
                      <Plane className="w-3 h-3" />
                      <span className="text-xs">None</span>
                    </div>
                  );
                }
                
                return (
                  <div className="flex flex-wrap gap-1">
                    {airports.slice(0, 2).map((airport, idx) => (
                      <SimpleTooltip
                        key={airport.iata}
                        content={`${airport.name} - ${airport.distance_km.toFixed(1)}km away (rank ${airport.rank})`}
                        side="top"
                      >
                        <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                          <Plane className="w-3 h-3" />
                          <span className="font-medium">{airport.iata}</span>
                          <span className="text-blue-500">{airport.distance_km.toFixed(0)}km</span>
                        </div>
                      </SimpleTooltip>
                    ))}
                    {airports.length > 2 && (
                      <div className="relative">
                        <SimpleTooltip
                          content={
                            <div className="space-y-1">
                              <div className="font-medium text-xs">Additional Airports:</div>
                              {airports.slice(2).map((airport) => (
                                <div key={airport.iata} className="flex items-center justify-between gap-2 text-xs">
                                  <div className="flex items-center gap-1">
                                    <Plane className="w-3 h-3" />
                                    <span className="font-medium">{airport.iata}</span>
                                    <span className="text-gray-500">{airport.name}</span>
                                  </div>
                                  <span className="text-gray-600">{airport.distance_km.toFixed(1)}km</span>
                                </div>
                              ))}
                            </div>
                          }
                          side="top"
                          delayDuration={0}
                        >
                          <button
                            type="button"
                            onClick={(e) => toggleTooltip(`${record.id}-airports`, e)}
                            className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs cursor-pointer hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation"
                          >
                            +{airports.length - 2}
                          </button>
                        </SimpleTooltip>
                        
                        {/* Mobile/Click tooltip overlay */}
                        {activeTooltip === `${record.id}-airports` && (
                          <div className="absolute bottom-full left-0 mb-2 z-50 bg-gray-800 text-white text-xs rounded-md shadow-lg p-2 min-w-[200px]">
                            <div className="space-y-1">
                              <div className="font-medium text-xs text-gray-200">Additional Airports:</div>
                              {airports.slice(2).map((airport) => (
                                <div key={airport.iata} className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1">
                                    <Plane className="w-3 h-3" />
                                    <span className="font-medium">{airport.iata}</span>
                                    <span className="text-gray-300">{airport.name}</span>
                                  </div>
                                  <span className="text-gray-400">{airport.distance_km.toFixed(1)}km</span>
                                </div>
                              ))}
                            </div>
                            {/* Arrow */}
                            <div className="absolute top-full left-4 w-2 h-2 bg-gray-800 rotate-45 transform -mt-1"></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              }
            },
            { 
              key: 'actions', 
              title: 'Actions',
              align: 'right',
              render: (_, record) => (
                <TableActions>
                  <TableActionButton icon={FilePen} onClick={() => openEdit(record)}>
                    Edit
                  </TableActionButton>
                  <TableActionButton variant="destructive" icon={UserMinus} onClick={() => setDeleteConfirmId(record.id)}>
                    Remove
                  </TableActionButton>
                </TableActions>
              )
            }
          ]}
          searchable={true}
          searchPlaceholder="Search attendees..."
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 50, 100]
          }}
          loading={loading}
          emptyText="No attendees added yet"
          serverSide={true}
          totalItems={data.pagination.totalItems}
          onPaginate={(newPage, newPageSize) => {
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setPage(1); // Reset to first page when changing page size
            } else {
              setPage(newPage);
            }
          }}
        />
      )}

      {/* Attendee Modal */}
      <Modal 
        isOpen={modalOpen} 
        onClose={closeModal} 
        title={editingId ? 'Edit Attendee' : 'Add Attendee'}
        description={editingId ? 'Update attendee information' : 'Add a new attendee to this event'}
      >
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">First Name *</label>
              <Input 
                value={form.first_name} 
                onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))} 
                placeholder="e.g., John"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Last Name *</label>
              <Input 
                value={form.last_name} 
                onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))} 
                placeholder="e.g., Smith"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <Input 
              type="email"
              value={form.email} 
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} 
              placeholder="e.g., john@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <Input 
              value={form.phone} 
              onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} 
              placeholder="e.g., +1 (555) 123-4567"
            />
          </div>

          {/* Location Section */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Location Information</h4>
            <p className="text-xs text-gray-600 mb-4">
              Location data helps calculate flight costs. Start typing to search for locations.
            </p>
            
            <div className="space-y-4">
              {/* Location Autocomplete */}
              <div>
                <label className="block text-sm font-medium mb-1">Search Location</label>
                <LocationAutocomplete
                  value={locationInput}
                  onChange={setLocationInput}
                  onLocationSelect={handleLocationSelect}
                  placeholder="Type city name or address (e.g., New York or 123 Main St, NYC)"
                  types="address"
                />
                {isLocationDisabled && (
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-green-600">✓ Location auto-populated from Google Places</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearLocation}
                    >
                      Clear & Edit Manually
                    </Button>
                  </div>
                )}
              </div>

              {/* Auto-populated fields (disabled when location selected) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">City</label>
                  <Input 
                    value={form.city} 
                    onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} 
                    placeholder="e.g., New York"
                    disabled={isLocationDisabled}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State/Region</label>
                  <Input 
                    value={form.region} 
                    onChange={(e) => setForm(f => ({ ...f, region: e.target.value }))} 
                    placeholder="e.g., NY, California, Ontario"
                    disabled={isLocationDisabled}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Country Code</label>
                  <Input 
                    value={form.country_iso2} 
                    onChange={(e) => setForm(f => ({ ...f, country_iso2: e.target.value.toUpperCase() }))} 
                    placeholder="e.g., US, CA, GB"
                    maxLength={2}
                    disabled={isLocationDisabled}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Postal Code</label>
                  <Input 
                    value={form.postal_code} 
                    onChange={(e) => setForm(f => ({ ...f, postal_code: e.target.value }))} 
                    placeholder="e.g., 10001, 90210"
                    disabled={isLocationDisabled}
                  />
                </div>
              </div>
              
              {/* Address field always editable */}
              <div>
                <label className="block text-sm font-medium mb-1">Full Address (Optional Override)</label>
                <Input 
                  value={form.address_line} 
                  onChange={(e) => setForm(f => ({ ...f, address_line: e.target.value }))} 
                  placeholder="e.g., 123 Main St, New York, NY 10001"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional: Provide specific address if different from the selected location above
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              isLoading={saving}
              disabled={!form.first_name.trim() || !form.last_name.trim()}
            >
              {editingId ? 'Save Changes' : 'Add Attendee'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        isOpen={!!deleteConfirmId} 
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={() => deleteConfirmId && remove(deleteConfirmId)}
        title="Remove Attendee" 
        description="Are you sure you want to remove this attendee from the event? This action cannot be undone."
        confirmText="Remove" 
        confirmVariant="danger"
      />

      {/* Import Modal */}
      {eventId && (
        <AttendeeImport
          eventId={eventId}
          isOpen={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          onImportComplete={() => {
            load(); // Refresh the attendee list
            setImportModalOpen(false);
          }}
        />
      )}

    </PageContainer>
  );
}
